import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './lib/auth';
import { CartProvider } from './lib/cart';
import { StoreProvider } from './lib/store';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { CartDrawer } from './components/CartDrawer';
import { Spinner } from './components/ui';
import { HomePage } from './pages/Home';
import { CollectionPage } from './pages/Collection';
import { ProductPage } from './pages/Product';
import { CheckoutPage } from './pages/Checkout';
import { OrderPage } from './pages/Order';
import { AccountPage } from './pages/Account';
import {
  AboutPage,
  CollectionsIndexPage,
  NotFoundPage,
  PrivacyPage,
  ShippingPage,
} from './pages/Static';

// The admin area is a separate bundle: no shopper ever downloads it.
const AdminApp = lazy(() =>
  import('./admin/AdminApp').then((m) => ({ default: m.AdminApp })),
);

/** Reset scroll on navigation — a SPA keeps the old position otherwise. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

function StorefrontLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-background focus:px-4 focus:py-2 focus:underline"
      >
        Skip to content
      </a>
      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}

function PageFallback() {
  return (
    <div className="flex justify-center py-32">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

export function App() {
  return (
    <StoreProvider>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <Routes>
            <Route element={<StorefrontLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/collections" element={<CollectionsIndexPage />} />
              <Route path="/collections/:slug" element={<CollectionPage />} />
              <Route path="/products/:slug" element={<ProductPage />} />
              <Route path="/search" element={<CollectionPage searchMode />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/orders/:orderId" element={<OrderPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/pages/about" element={<AboutPage />} />
              <Route path="/pages/shipping-policies" element={<ShippingPage />} />
              <Route path="/pages/privacy" element={<PrivacyPage />} />
              {/* Keep old Shopify links working. */}
              <Route
                path="/policies/shipping-policy"
                element={<Navigate to="/pages/shipping-policies" replace />}
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route
              path="/admin/*"
              element={
                <Suspense fallback={<PageFallback />}>
                  <AdminApp />
                </Suspense>
              }
            />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </StoreProvider>
  );
}
